/* ==========================================================================
   VANGUARDA — theme.js
   Vanilla, dependency-free. Custom elements + a few small behaviours.
   ========================================================================== */
(function () {
  'use strict';

  const VG = window.VG || {};
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  /* ---------------------------------------------------------------- utils */
  function formatMoney(cents) {
    const fmt = VG.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toLocaleString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toString());
  }

  let toastTimer;
  function toast(message) {
    const el = document.getElementById('vg-toast');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
  }
  window.vgToast = toast;

  function lockScroll(on) {
    document.body.classList.toggle('no-scroll', !!on);
  }

  function trapFocus(container) {
    const focusables = $$(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      container
    ).filter((el) => el.offsetParent !== null);
    if (!focusables.length) return () => {};
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', handler);
    first.focus();
    return () => container.removeEventListener('keydown', handler);
  }

  /* ------------------------------------------------------------- <vg-drawer> */
  class VgDrawer extends HTMLElement {
    connectedCallback() {
      this.releaseFocus = null;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target.matches('.drawer__overlay')) {
          this.close();
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });
      $$(`[data-drawer-open="${this.id}"]`).forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.opener = btn;
          this.open();
        })
      );
    }
    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      const panel = $('.drawer__panel', this);
      if (panel) this.releaseFocus = trapFocus(panel);
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (this.releaseFocus) this.releaseFocus();
      if (this.opener) this.opener.focus();
    }
  }
  customElements.define('vg-drawer', VgDrawer);

  /* --------------------------------------------------------------- header */
  class VgHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.ticking = false;
      window.addEventListener('scroll', () => {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => this.onScroll());
      }, { passive: true });
    }
    onScroll() {
      const y = window.scrollY;
      const goingDown = y > this.lastY;
      if (y > 260 && goingDown) this.classList.add('header--hidden');
      else this.classList.remove('header--hidden');
      this.lastY = y;
      this.ticking = false;
    }
  }
  customElements.define('vg-header', VgHeader);

  /* --------------------------------------------------------- announcement */
  class VgAnnouncement extends HTMLElement {
    connectedCallback() {
      const slides = $$('.announcement__slide', this);
      if (slides.length < 2) return;
      let i = 0;
      const speed = parseInt(this.dataset.speed || '5', 10) * 1000;
      setInterval(() => {
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }, speed);
    }
  }
  customElements.define('vg-announcement', VgAnnouncement);

  /* ----------------------------------------------------------------- qty */
  class VgQty extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      $$('button', this).forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const step = btn.dataset.qty === 'plus' ? 1 : -1;
          const min = parseInt(this.input.min || '1', 10);
          const next = Math.max(min, (parseInt(this.input.value, 10) || min) + step);
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  customElements.define('vg-qty', VgQty);

  /* ------------------------------------------------------------- reveal */
  function initReveal() {
    if (!document.body.classList.contains('reveal-enabled')) return;
    if (!('IntersectionObserver' in window)) {
      $$('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
    );
    $$('[data-reveal]').forEach((el, i) => {
      if (!el.style.getPropertyValue('--reveal-delay')) {
        el.style.setProperty('--reveal-delay', `${Math.min(i % 6, 5) * 70}ms`);
      }
      io.observe(el);
    });
  }

  /* ------------------------------------------------------- discount ladder */
  /* The ladder lives inside [data-cart-contents], which theme.js replaces
     wholesale on every cart change. That is why it never animated: the bar was
     rebuilt at its final width, and a transition needs a value to move FROM.

     This hands the motion across the swap. Read the old ladder before the
     innerHTML goes, rewind the new one onto those values, flush, then write the
     real values a frame later. Everything else — the fill, the slide, the dot
     turning solid — is CSS doing what it was always going to do, once it has
     two values to work with. */
  const reduceMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const Ladder = {
    read(scope) {
      const el = scope && scope.querySelector('.vgl');
      if (!el) return null;
      return {
        start: el.style.getPropertyValue('--vgl-start').trim() || '0',
        reached: Number(el.dataset.reached),
        steps: $$('.vgl__step', el).map((s) => {
          const line = s.querySelector('.vgl__line');
          return {
            fill: line ? line.style.getPropertyValue('--fill').trim() : '0%',
            state: s.dataset.state || 'future'
          };
        })
      };
    },

    play(scope, prev) {
      if (!prev || reduceMotion()) return;
      const el = scope && scope.querySelector('.vgl');
      if (!el) return;
      const steps = $$('.vgl__step', el);
      /* The merchant changed the tiers between renders. There is no sensible
         frame-to-frame mapping, so show the truth rather than invent motion. */
      if (steps.length !== prev.steps.length) return;

      const start = el.style.getPropertyValue('--vgl-start').trim() || '0';
      const reached = Number(el.dataset.reached);
      const target = steps.map((s) => {
        const line = s.querySelector('.vgl__line');
        return {
          fill: line ? line.style.getPropertyValue('--fill').trim() : '0%',
          state: s.dataset.state || 'future'
        };
      });

      const still =
        start === prev.start &&
        target.every(
          (t, i) => t.fill === prev.steps[i].fill && t.state === prev.steps[i].state
        );
      if (still) return;

      const write = (vals) =>
        steps.forEach((s, i) => {
          const line = s.querySelector('.vgl__line');
          if (line) line.style.setProperty('--fill', vals[i].fill);
          s.dataset.state = vals[i].state;
        });

      el.classList.add('vgl--no-anim');
      el.style.setProperty('--vgl-start', prev.start);
      write(prev.steps);
      /* Reading a layout property forces the rewind to be committed as its own
         style change. Without this the browser batches the rewind and the
         rewrite into one frame, computes a single before/after pair, and the
         bar snaps exactly as it used to. */
      void el.offsetWidth;
      el.classList.remove('vgl--no-anim');

      requestAnimationFrame(() => {
        el.style.setProperty('--vgl-start', start);
        write(target);
        if (reached > prev.reached) this.celebrate(el, steps, prev.reached, reached);
      });
    },

    celebrate(el, steps, from, to) {
      for (let i = from + 1; i <= to && i < steps.length; i += 1) {
        const step = steps[i];
        step.classList.remove('is-just-unlocked');
        void step.offsetWidth;
        step.classList.add('is-just-unlocked');
        /* A fixed timer rather than animationend: three animations run on this
           step and animationend fires for each, so the first one to finish
           would strip the class off the other two. */
        setTimeout(() => step.classList.remove('is-just-unlocked'), 1100);
      }

      const say = el.parentElement && el.parentElement.querySelector('.cart-reward__text');
      if (say) {
        say.classList.remove('is-swapping');
        void say.offsetWidth;
        say.classList.add('is-swapping');
        setTimeout(() => say.classList.remove('is-swapping'), 520);
      }

      /* One short tap. On a phone the drawer sits under the thumb and the
         ladder is at the top of it, so the unlock can happen entirely outside
         where the shopper is looking. Vibration is the only cue that does not
         depend on where their eyes are. */
      if (navigator.vibrate) {
        try {
          navigator.vibrate(18);
        } catch (err) {
          /* Chrome throws if the gesture is too far back; nothing to do. */
        }
      }
    }
  };

  /* ---------------------------------------------------------------- cart */
  const Cart = {
    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw data;
      return data;
    },
    async add(items) {
      /* 18/10 - pede a secao do carrinho JUNTO do add. A API de carrinho do
         Shopify aceita `sections` e devolve o HTML ja renderizado na mesma
         resposta. Antes eram duas idas ao servidor em sequencia (adiciona,
         depois busca a secao) e o cliente ficava olhando a gaveta parada entre
         as duas. Agora e uma so.

         Se a resposta vier sem `sections`, render() cai no caminho de busca de
         sempre - nada quebra. */
      const data = await this.request(VG.routes.cart_add_url + '.js', {
        items,
        sections: 'cart-drawer',
        sections_url: window.location.pathname
      });
      await this.render(null, data.sections && data.sections['cart-drawer']);
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      return data;
    },
    async change(payload) {
      const data = await this.request(VG.routes.cart_change_url + '.js', payload);
      this.render(data);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },
    async refresh() {
      const res = await fetch(VG.routes.cart_url + '.js');
      const data = await res.json();
      this.render(data);
      return data;
    },
    setCount(n) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = n;
        el.hidden = n === 0;
      });
    },
    render(cart, sectionHtml) {
      /* change() already holds the authoritative cart, so its badge updates on
         the spot instead of waiting for the section. add() has no count to give
         and takes it from the fetched document below. */
      if (cart) this.setCount(cart.item_count);

      const drawer = document.getElementById('CartDrawer');
      let drawn = Promise.resolve();
      if (drawer) {
        const target = drawer.querySelector('[data-cart-contents]');
        const prev = Ladder.read(target);
        const body = target && target.querySelector('.drawer__body');
        const scrolled = body ? body.scrollTop : 0;

        const apply = (html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const fresh = parsed.querySelector('[data-cart-contents]');
          if (!fresh || !target) return;
          target.innerHTML = fresh.innerHTML;

          if (!cart) {
            const badge = parsed.querySelector('[data-cart-count]');
            if (badge) this.setCount(Number(badge.textContent) || 0);
          }
          /* Replacing the innerHTML throws the scroll position away, which
             sends a shopper editing the fourth line back to the first. */
          const nextBody = target.querySelector('.drawer__body');
          if (nextBody && scrolled) nextBody.scrollTop = scrolled;

          Ladder.play(target, prev);
        };

        if (sectionHtml) {
          apply(sectionHtml);
        } else {
          drawn = fetch(`${window.location.pathname}?section_id=cart-drawer`)
            .then((r) => r.text())
            .then(apply)
            .catch(() => {});
        }
      }
      const cartPage = $('[data-cart-page]');
      if (cartPage && cartPage.dataset.sectionId) {
        fetch(`${VG.routes.cart_url}?section_id=${cartPage.dataset.sectionId}`)
          .then((r) => r.text())
          .then((html) => {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const fresh = parsed.querySelector('[data-cart-page]');
            if (fresh) cartPage.innerHTML = fresh.innerHTML;
          })
          .catch(() => window.location.reload());
      }
      return drawn;
    }
  };
  window.vgCart = Cart;

  /* ------------------------------------------------------- <vg-add-form> */
  class VgAddForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.button = $('[type="submit"]', this.form);
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      if (VG.cartType !== 'drawer') return;
      e.preventDefault();
      const id = this.form.querySelector('[name="id"]');
      if (!id || !id.value) return;
      const qtyInput = this.form.querySelector('[name="quantity"]');
      const original = this.button ? this.button.textContent : '';
      if (this.button) {
        this.button.setAttribute('aria-disabled', 'true');
        this.button.textContent = '…';
      }
      /* A gaveta abre ANTES do pedido so quando ja ha alguma coisa nela para
         ver: a sacola aparece como estava e a escada se enche quando a
         resposta chega.

         18/10 - com a sacola VAZIA isso fazia o contrario do pretendido.
         Vazia e justamente o estado da primeira compra de todo cliente novo,
         e o que ele via ao tocar em adicionar era a tela "seu carrinho esta
         vazio" - no exato instante em que acabou de adicionar. Parece que o
         botao falhou, e e ai que a pessoa desiste.

         Com a sacola vazia a gaveta passa a abrir depois, ja com o produto
         dentro, e o botao fica em "..." como sinal de que algo acontece. Com
         o `sections` no pedido essa espera e uma unica ida ao servidor. */
      const drawer = document.getElementById('CartDrawer');
      const countEl = document.querySelector('[data-cart-count]');
      const hadItems = countEl ? (Number(countEl.textContent) || 0) > 0 : false;
      if (drawer && hadItems) drawer.open();
      try {
        await Cart.add([
          {
            id: Number(id.value),
            quantity: qtyInput ? Number(qtyInput.value) || 1 : 1
          }
        ]);
        if (drawer) {
          if (!hadItems) drawer.open();
        } else {
          toast('Added to bag');
        }
      } catch (err) {
        toast((err && err.description) || 'Could not add to bag');
      } finally {
        if (this.button) {
          this.button.removeAttribute('aria-disabled');
          this.button.textContent = original;
        }
      }
    }
  }
  customElements.define('vg-add-form', VgAddForm);

  /* -------------------------------------------------------- cart line UI */
  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      await Cart.change({ line: Number(remove.dataset.cartRemove), quantity: 0 });
      return;
    }
    const quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      const original = quick.textContent;
      quick.setAttribute('aria-disabled', 'true');
      quick.textContent = '…';
      try {
        await Cart.add([{ id: Number(quick.dataset.quickAdd), quantity: 1 }]);
        const drawer = document.getElementById('CartDrawer');
        if (drawer) drawer.open();
        else toast('Added to bag');
      } catch (err) {
        toast('Sold out');
      } finally {
        quick.removeAttribute('aria-disabled');
        quick.textContent = original;
      }
    }
  });

  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-cart-qty]');
    if (!input) return;
    await Cart.change({ line: Number(input.dataset.cartQty), quantity: Number(input.value) });
  });

  /* --------------------------------------------------- <vg-variant-picker> */
  class VgVariantPicker extends HTMLElement {
    connectedCallback() {
      this.section = this.dataset.section;
      this.url = this.dataset.url;
      const raw = $('[type="application/json"]', this);
      this.variants = raw ? JSON.parse(raw.textContent) : [];
      this.addEventListener('change', () => this.onChange());
    }
    selectedOptions() {
      return $$('input[type="radio"]:checked, select', this).map((el) => el.value);
    }
    onChange() {
      const opts = this.selectedOptions();
      const match = this.variants.find((v) =>
        v.options.every((o, i) => o === opts[i])
      );
      this.updateLabels(opts);
      if (!match) {
        this.setUnavailable();
        return;
      }
      this.current = match;
      this.updateURL(match);
      this.updateInput(match);
      this.updatePrice(match);
      this.updateButton(match);
      this.updateMedia(match);
    }
    updateLabels(opts) {
      $$('[data-option-selected]', this).forEach((el, i) => {
        if (opts[i]) el.textContent = opts[i];
      });
    }
    updateURL(variant) {
      if (!variant || !this.url) return;
      window.history.replaceState({}, '', `${this.url}?variant=${variant.id}`);
    }
    updateInput(variant) {
      const input = document.querySelector(`#variant-id-${this.section}`);
      if (input) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    updatePrice(variant) {
      const price = document.querySelector(`#price-${this.section}`);
      if (!price) return;
      let html = `<span class="${variant.compare_at_price > variant.price ? 'price--sale' : ''}">${formatMoney(variant.price)}</span>`;
      if (variant.compare_at_price > variant.price) {
        html = `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` + html;
      }
      price.innerHTML = html;
    }
    updateButton(variant) {
      const btn = document.querySelector(`#add-button-${this.section}`);
      if (!btn) return;
      if (variant.available) {
        btn.removeAttribute('aria-disabled');
        btn.textContent = VG.strings.addToCart;
      } else {
        btn.setAttribute('aria-disabled', 'true');
        btn.textContent = VG.strings.soldOut;
      }
    }
    setUnavailable() {
      const btn = document.querySelector(`#add-button-${this.section}`);
      if (btn) {
        btn.setAttribute('aria-disabled', 'true');
        btn.textContent = VG.strings.unavailable;
      }
    }
    updateMedia(variant) {
      if (!variant.featured_media) return;
      const target = document.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      if (!target) return;
      const gallery = target.closest('.product__gallery');
      if (gallery) gallery.prepend(target);
    }
  }
  customElements.define('vg-variant-picker', VgVariantPicker);

  /* ------------------------------------------------------ predictive search */
  class VgSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-search-results]', this);
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(), 280);
      });
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      try {
        const url = `${VG.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product,collection,article&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url);
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('[data-search-results]');
        this.results.innerHTML = fresh ? fresh.innerHTML : '';
      } catch (err) {
        this.results.innerHTML = '';
      }
    }
  }
  customElements.define('vg-search', VgSearch);

  /* ------------------------------------------------------------ marquee */
  function initMarquee() {
    $$('.marquee__track').forEach((track) => {
      if (track.dataset.cloned) return;
      track.dataset.cloned = 'true';
      const group = track.firstElementChild;
      if (group) track.appendChild(group.cloneNode(true));
    });
  }

  /* ---------------------------------------------------------- collection */
  function initSort() {
    const select = $('[data-sort-by]');
    if (!select) return;
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  function initFilters() {
    $$('[data-filter-form]').forEach((form) => {
      if (form.dataset.bound) return;
      form.dataset.bound = '1';
      // the drawer version waits for its Apply button; the sidebar submits live
      if ('filterManual' in form.dataset) return;
      form.addEventListener('change', () => form.submit());
    });
  }

  /* ------------------------------------------------------------- gallery */
  function initGalleryZoom() {
    $$('[data-zoom]').forEach((img) => {
      img.addEventListener('click', () => {
        img.classList.toggle('is-zoomed');
      });
    });
  }

  /* ---------------------------------------------------------------- boot */
  function init() {
    initReveal();
    initMarquee();
    initSort();
    initFilters();
    initGalleryZoom();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
